import type { ReactNode } from 'react'
import { buildHighlightSvg } from './scene'
import type { HighlightVisualEvidence, KeyHighlight } from './types'
import './keyHighlights.css'

const stateLabel: Record<KeyHighlight['changeState'], string> = {
  new: 'New since review', worsened: 'Worsened', improved: 'Improved', still_priority: 'Still priority', resolved: 'Resolved', current_priority: 'Current priority',
}

const sourceLabel = (source: string): string => source.replace(/^quickbooks_cfo_/, 'cfo_')

export function KeyHighlightsSection({
  highlights,
  evidence,
  onDrill,
  rejections,
}: {
  highlights: KeyHighlight[]
  evidence: HighlightVisualEvidence[]
  onDrill?: (highlight: KeyHighlight) => void
  /** Why candidates were dropped, so an empty section can explain itself. */
  rejections?: Array<{ insightId: string; reason: string }>
}): ReactNode {
  if (!highlights.length) {
    // Never return null. A correct refusal and a broken page looked identical,
    // which is exactly why an empty board pack went unnoticed: the section
    // simply vanished. Say what happened and why.
    const reasons = (rejections || []).map((entry) => entry.reason)
    // Distinguish "we had candidates and withheld them" from "nothing reached
    // us at all". The second is a data or pipeline condition, not a judgement,
    // and reporting it as "no findings" sent us hunting in the wrong place.
    const counted = reasons.reduce<Record<string, number>>((acc, reason) => {
      acc[reason] = (acc[reason] || 0) + 1
      return acc
    }, {})
    const note = reasons.includes('open_period')
      ? 'The reporting period is still posting, so period-over-period findings are withheld until the month closes.'
      : reasons.length
        ? `${reasons.length} candidate${reasons.length === 1 ? '' : 's'} did not meet the evidence bar.`
        : 'No insight cards reached this report — the period may still be syncing, or the detectors found nothing to report.'
    return (
      <section className="fg-key-highlights fg-key-highlights--empty" aria-labelledby="fg-key-highlights-title">
        <h2 id="fg-key-highlights-title">Key Highlights</h2>
        <p className="fg-key-highlights-empty-note">{note}</p>
        {reasons.length ? (
          <ul className="fg-key-highlights-empty-reasons">
            {Object.entries(counted).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
              <li key={reason}><b>{count}</b> {reason.replace(/_/g, ' ')}</li>
            ))}
          </ul>
        ) : null}
      </section>
    )
  }
  const evidenceById = new Map(evidence.map((item) => [item.id, item]))
  return (
    <section className={`fg-key-highlights fg-key-highlights--${Math.min(3, highlights.length)}`} aria-labelledby="fg-key-highlights-title">
      <div className="fg-key-highlights__heading">
        <div><span className="fg-key-highlights__eyebrow">Board briefing</span><h2 id="fg-key-highlights-title">Key Highlights</h2></div>
        <p>The changes and decisions that matter most right now.</p>
      </div>
      <div className="fg-key-highlights__grid">
        {highlights.map((highlight, index) => {
          const visualEvidence = evidenceById.get(highlight.evidenceRef)
          if (!visualEvidence) return null
          return (
            <article
              key={highlight.id}
              className={`fg-key-highlight ${index === 0 && highlights.length === 3 ? 'fg-key-highlight--hero' : ''}`}
              aria-label={highlight.accessibilitySummary}
              data-visual-template={highlight.templateId}
              data-visual-family={highlight.visualFamily}
              data-visual-form={highlight.visualForm}
            >
              <div className="fg-key-highlight__meta"><span data-state={highlight.changeState}>{stateLabel[highlight.changeState]}</span><span>{visualEvidence.freshness === 'current' ? `Through ${highlight.dataThrough || 'latest data'}` : visualEvidence.freshness}</span></div>
              <div className="fg-key-highlight__value">{highlight.valueDisplay}</div>
              <h3>{highlight.headline}</h3>
              <div className="fg-key-highlight__visual" dangerouslySetInnerHTML={{ __html: buildHighlightSvg(highlight.templateId, visualEvidence) }} />
              <p>{highlight.implication}</p>
              {highlight.principalFinding ? (
                <div className="fg-key-highlight__finding"><strong>What moved</strong><span>{highlight.principalFinding}</span></div>
              ) : null}
              {onDrill ? <button type="button" onClick={() => onDrill(highlight)}>Open supporting detail →</button> : null}
              <small>{visualEvidence.provenanceState === 'governed' ? 'Governed source' : visualEvidence.provenanceState} · {visualEvidence.sourceLineage.map(sourceLabel).join(' · ')}</small>
            </article>
          )
        })}
      </div>
    </section>
  )
}
